import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiCheck, FiMail, FiType, FiMaximize, FiDroplet, FiMessageSquare } from 'react-icons/fi';

const ContactFormPopup = ({ onClose, onUpdate, currentStyles }) => {
  const [styles, setStyles] = useState({
    destinationEmail: currentStyles.destinationEmail || '',
    buttonText: currentStyles.buttonText || 'Send Message',
    buttonBgColor: currentStyles.buttonBgColor || '#2dd4bf',
    buttonTextColor: currentStyles.buttonTextColor || '#ffffff',
    formBgColor: currentStyles.formBgColor || '#ffffff',
    inputBgColor: currentStyles.inputBgColor || '#f9fafb',
    textColor: currentStyles.textColor || '#111827',
    borderRadius: currentStyles.borderRadius !== undefined ? currentStyles.borderRadius : 12
  });

  const colors = ['#ffffff', '#f9fafb', '#f3f4f6', '#111827', '#2dd4bf', '#3b82f6', '#ef4444', '#f59e0b'];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
              <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-gray-800">Contact Form Settings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          
          {/* Destination Email */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiMail} className="mr-2" /> Destination Email
            </label>
            <input
              type="email"
              value={styles.destinationEmail}
              onChange={(e) => setStyles({ ...styles, destinationEmail: e.target.value })}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm font-medium"
              placeholder="Where should emails go?"
            />
            <p className="text-[9px] text-gray-400 font-medium">Form submissions will be sent to this email address.</p>
          </div>

          <div className="h-px bg-gray-100" />

          {/* Button Text */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiType} className="mr-2" /> Button Label
            </label>
            <input
              type="text"
              value={styles.buttonText}
              onChange={(e) => setStyles({ ...styles, buttonText: e.target.value })}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm font-bold"
              placeholder="Send Message"
            />
          </div>

          {/* Corner Radius Slider */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiMaximize} className="mr-2" /> Corner Rounding
              </label>
              <span className="text-xs font-bold text-teal-500">{styles.borderRadius}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="40"
              value={styles.borderRadius}
              onChange={(e) => setStyles({ ...styles, borderRadius: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-teal-500"
            />
          </div>

          {/* Form Colors */}
          <div className="space-y-4">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiDroplet} className="mr-2" /> Formatting & Colors
            </label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-500 uppercase">Form Background</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={styles.formBgColor} onChange={(e) => setStyles({ ...styles, formBgColor: e.target.value })} className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{styles.formBgColor}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-500 uppercase">Input Background</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={styles.inputBgColor} onChange={(e) => setStyles({ ...styles, inputBgColor: e.target.value })} className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{styles.inputBgColor}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-500 uppercase">Text Color</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={styles.textColor} onChange={(e) => setStyles({ ...styles, textColor: e.target.value })} className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{styles.textColor}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-500 uppercase">Button Background</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={styles.buttonBgColor} onChange={(e) => setStyles({ ...styles, buttonBgColor: e.target.value })} className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{styles.buttonBgColor}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-500 uppercase">Button Text Color</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={styles.buttonTextColor} onChange={(e) => setStyles({ ...styles, buttonTextColor: e.target.value })} className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{styles.buttonTextColor}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 flex space-x-3 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Discard
          </button>
          <button
            onClick={() => { onUpdate(styles); onClose(); }}
            className="flex-1 py-3 bg-teal-500 text-white font-bold rounded-xl shadow-lg shadow-teal-100 hover:bg-teal-600 transition-all flex justify-center items-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Apply Changes</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactFormPopup;